import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart3, Calendar, Loader2, Printer, Share2, FileText,
  Search, Eye, ShoppingCart, User, Wallet, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { smartMatch } from "../../utils/arabicSearch";
import InvoiceModal from "../InvoiceModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const splitDate = (iso) => {
  if (!iso) return { day: "—", time: "" };
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString("en-GB", { year: "2-digit", month: "2-digit", day: "2-digit" }),
      time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch { return { day: iso, time: "" }; }
};

const SUPPORT_PHONES = [
  { local: "07717637525", label: "خدمة العملاء" },
  { local: "07842220088", label: "الدعم الفني" },
];

const SOURCES = [
  { id: "all", label: "الكل", color: "bg-[#1E3A5F]" },
  { id: "app", label: "طلبات التطبيق", color: "bg-[#3B82F6]" },
  { id: "supervisor", label: "بيع المشرف", color: "bg-[#7C3AED]" },
  { id: "direct", label: "بيع مباشر", color: "bg-[#F5A623]" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const SalesInvoicesReportTab = () => {
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(todayISO());
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewOrder, setViewOrder] = useState(null); // full order object for modal

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/reports/sales-invoices`, {
        params: { date_from: from, date_to: to, source, q },
      });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل التقرير");
    } finally { setLoading(false); }
  };

  // Re-fetch on filter change. Debounce `q` slightly so the user can
  // finish typing before we hit the backend — 350ms feels natural.
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [from, to, source]);
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  // Server already filters by q; no additional client-side filter needed.
  const filtered = data?.invoices || [];

  const openInvoice = async (row) => {
    try {
      const { data: full } = await axios.get(`${API}/admin/orders/${row.id}`);
      setViewOrder(full);
    } catch (e) {
      toast.error("فشل جلب الفاتورة");
    }
  };

  const buildPrintHtml = () => {
    const dateStr = new Date().toLocaleString("ar-IQ", { dateStyle: "medium", timeStyle: "short" });
    const srcLabel = SOURCES.find((s) => s.id === source)?.label || "الكل";
    const rows = filtered.map((r, i) => {
      const dt = splitDate(r.created_at);
      const payCls = r.payment_status === "paid" ? "pay-green"
                    : r.payment_status === "partial" ? "pay-orange" : "pay-red";
      const payLbl = r.payment_status === "paid" ? "مسدَّدة"
                    : r.payment_status === "partial" ? "جزئي" : "غير مسدَّدة";
      const srcCls = r.source === "بيع المشرف" ? "src-purple"
                    : r.source === "بيع مباشر" ? "src-orange" : "src-blue";
      return `<tr class="${srcCls}">
        <td class="c" dir="ltr">${i + 1}</td>
        <td class="c" dir="ltr"><b>#${r.invoice_number || "—"}</b></td>
        <td class="c" dir="ltr">
          <div><b>${dt.day}</b></div>
          <div style="font-size:9px;color:#5C6E64">${dt.time}</div>
        </td>
        <td>
          <div style="font-weight:800">${(r.customer_name || "—").replace(/</g, "&lt;")}</div>
          ${r.customer_market_name ? `<div style="font-size:10px;color:#5C6E64">🏪 ${r.customer_market_name.replace(/</g, "&lt;")}</div>` : ""}
          <div style="font-size:10px;color:#5C6E64" dir="ltr">${(r.customer_phone || "").replace(/</g, "&lt;")}</div>
        </td>
        <td class="c">${r.source}${r.source_extra ? `<div style="font-size:9px;color:#5C6E64">${r.source_extra.replace(/</g,"&lt;")}</div>` : ""}</td>
        <td class="c" dir="ltr">${r.items_count}</td>
        <td class="c" dir="ltr"><b>${fmt(r.total)}</b></td>
        <td class="c amt-green" dir="ltr">${fmt(r.paid)}</td>
        <td class="c amt-red" dir="ltr">${fmt(r.remaining)}</td>
        <td class="c"><span class="pill ${payCls}">${payLbl}</span></td>
      </tr>`;
    }).join("");

    const s = data.summary;
    const supportHtml = SUPPORT_PHONES.map((p) => `${p.label}: <b dir="ltr">${p.local}</b>`).join(" · ");

    return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"/>
      <title>تقرير مبيعات الفواتير — جملة أون لاين</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: 'Tajawal','Tahoma','Segoe UI',Arial,sans-serif; padding: 12px; color: #111; direction: rtl; margin: 0; }
        .header { display: flex; justify-content: space-between; align-items: flex-start;
          padding-bottom: 12px; border-bottom: 3px solid #F5A623; margin-bottom: 12px; }
        .brand h1 { color: #E85D45; margin: 0; font-size: 22px; }
        .brand p { margin: 4px 0 0; color: #5C6E64; font-size: 12px; }
        .date-box { text-align: left; font-size: 11px; color: #5C6E64;
          background: #F8FAFC; border: 1px solid #E4EBE6; border-radius: 10px; padding: 8px 12px; }
        .date-box b { display: block; color: #1E3A5F; font-size: 13px; }
        .filter-line { font-size: 11px; color: #5C6E64; margin-bottom: 8px; }
        .filter-line b { color: #1E3A5F; }
        table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
        thead th { background: #1E3A5F; color: #fff; padding: 6px 4px; text-align: right; }
        thead th.c { text-align: center; }
        tbody td { border: 1px solid #E4EBE6; padding: 5px; vertical-align: middle; }
        tbody td.c { text-align: center; }
        .src-blue td { background: #EFF6FF; }
        .src-orange td { background: #FFFBEB; }
        .src-purple td { background: #FAF5FF; }
        .amt-red { color: #B91C1C; font-weight: 900; }
        .amt-green { color: #15803D; font-weight: 900; }
        .pill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 800; }
        .pay-green { background: #DCFCE7; color: #15803D; }
        .pay-orange { background: #FEF3C7; color: #B45309; }
        .pay-red { background: #FEE2E2; color: #B91C1C; }
        .summary-blocks { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; margin-top: 12px; }
        .block { border-radius: 12px; padding: 12px 14px; color: #fff; text-align: center; }
        .block .lbl { font-size: 11px; opacity: 0.9; }
        .block .val { font-size: 22px; font-weight: 900; margin-top: 2px; }
        .b-blue { background: linear-gradient(90deg,#1E3A5F,#3B82F6); }
        .b-green { background: linear-gradient(90deg,#15803D,#22C55E); }
        .b-red { background: linear-gradient(90deg,#B91C1C,#DC2626); }
        .footer { margin-top: 20px; padding-top: 12px; border-top: 2px dashed #E4EBE6;
          text-align: center; font-size: 11px; color: #5C6E64; line-height: 1.7; }
        .footer .brand-line { color: #E85D45; font-weight: 900; font-size: 14px; }
      </style></head>
      <body>
        <div class="header">
          <div class="brand">
            <h1>جملة أون لاين</h1>
            <p>تقرير مبيعات الفواتير · Jumlaty Online</p>
          </div>
          <div class="date-box">
            <b>تاريخ الطباعة</b>
            <span dir="ltr">${dateStr}</span>
          </div>
        </div>
        <div class="filter-line">
          الفترة: <b>${from}</b> إلى <b>${to}</b> · المصدر: <b>${srcLabel}</b> · عدد الفواتير: <b>${fmt(filtered.length)}</b>
        </div>
        <table>
          <thead>
            <tr>
              <th class="c" style="width:30px">#</th>
              <th class="c" style="width:60px">رقم الفاتورة</th>
              <th class="c" style="width:75px">التاريخ</th>
              <th>الزبون</th>
              <th class="c" style="width:110px">المصدر</th>
              <th class="c" style="width:45px">أصناف</th>
              <th class="c" style="width:90px">الإجمالي</th>
              <th class="c" style="width:80px">المدفوع</th>
              <th class="c" style="width:80px">المتبقي</th>
              <th class="c" style="width:75px">الحالة</th>
            </tr>
          </thead>
          <tbody>
            ${rows || `<tr><td colspan="10" class="c" style="padding:20px;color:#5C6E64">لا توجد فواتير</td></tr>`}
          </tbody>
        </table>
        <div class="summary-blocks">
          <div class="block b-blue">
            <div class="lbl">💵 إجمالي المبيعات</div>
            <div class="val" dir="ltr">${fmt(s.total_sales)} د.ع</div>
          </div>
          <div class="block b-green">
            <div class="lbl">✅ المسدَّد / المستلَم نقداً</div>
            <div class="val" dir="ltr">${fmt(s.total_received)} د.ع</div>
          </div>
          <div class="block b-red">
            <div class="lbl">🔴 المتبقي (دين)</div>
            <div class="val" dir="ltr">${fmt(s.total_remaining)} د.ع</div>
          </div>
        </div>
        <div class="footer">
          <div class="brand-line">جملة أون لاين — Jumlaty Online</div>
          <div>${supportHtml}</div>
          <div>الموقع: jumlaty.online</div>
        </div>
        <script>window.onload=()=>setTimeout(()=>window.print(),300);</script>
      </body></html>`;
  };

  const handlePrint = () => {
    if (!filtered.length) { toast.error("لا توجد بيانات"); return; }
    const w = window.open("", "_blank", "width=1100,height=980");
    if (!w) { toast.error("فعّل النوافذ المنبثقة"); return; }
    w.document.open(); w.document.write(buildPrintHtml()); w.document.close(); w.focus();
  };

  const handleWhatsApp = () => {
    if (!filtered.length) { toast.error("لا توجد بيانات"); return; }
    const s = data.summary;
    const lines = [];
    lines.push("*📊 تقرير مبيعات الفواتير — جملة أون لاين*");
    lines.push(`الفترة: ${from} → ${to}`);
    lines.push(`المصدر: ${SOURCES.find(x=>x.id===source)?.label}`);
    lines.push(`عدد الفواتير: *${fmt(filtered.length)}*`);
    lines.push("─".repeat(22));
    filtered.slice(0, 40).forEach((r, i) => {
      const status = r.payment_status === "paid" ? "✅" : r.payment_status === "partial" ? "🟠" : "🔴";
      lines.push(`${i + 1}. #${r.invoice_number} · ${r.customer_name}`);
      lines.push(`   ${status} ${r.source} · ${fmt(r.total)} د.ع · متبقي ${fmt(r.remaining)}`);
    });
    if (filtered.length > 40) lines.push(`... و ${filtered.length - 40} فاتورة أخرى`);
    lines.push("─".repeat(22));
    lines.push(`*💵 إجمالي المبيعات: ${fmt(s.total_sales)} د.ع*`);
    lines.push(`*✅ المستلم: ${fmt(s.total_received)} د.ع*`);
    lines.push(`*🔴 المتبقي: ${fmt(s.total_remaining)} د.ع*`);
    lines.push("");
    lines.push("*جملة أون لاين — Jumlaty Online*");
    SUPPORT_PHONES.forEach((p) => lines.push(`${p.label}: ${p.local}`));
    const url = `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank");
  };

  const s = data?.summary || {};

  return (
    <div className="space-y-3" dir="rtl" data-testid="sales-report-tab">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-5 h-5" />
          <h3 className="font-bold text-lg">تقارير مبيعات الفواتير</h3>
        </div>
        <p className="text-xs opacity-95">
          كل فواتير البيع المؤكّدة (طلبات التطبيق + بيع المشرف + بيع مباشر) بالتاريخ والمصدر — مع مسدَّد / غير مسدَّد.
        </p>
      </div>

      {/* Date range */}
      <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[140px]">
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              data-testid="sales-from"
              className="w-full pr-9 pl-2 py-2 rounded-xl border-2 border-[#F5A623]/50 bg-white text-sm" />
          </div>
          <span className="text-[#5C6E64] font-bold">→</span>
          <div className="relative flex-1 min-w-[140px]">
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              data-testid="sales-to"
              className="w-full pr-9 pl-2 py-2 rounded-xl border-2 border-[#F5A623]/50 bg-white text-sm" />
          </div>
          <button onClick={fetchData} data-testid="sales-refresh"
            className="bg-[#1E3A5F] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> تحديث
          </button>
        </div>
        {/* Quick date presets */}
        <div className="flex flex-wrap gap-1.5 text-[10px]">
          <QuickDateBtn label="اليوم" onClick={() => { setFrom(todayISO()); setTo(todayISO()); }} />
          <QuickDateBtn label="أمس" onClick={() => { const d=daysAgoISO(1); setFrom(d); setTo(d); }} />
          <QuickDateBtn label="آخر ٧ أيام" onClick={() => { setFrom(daysAgoISO(6)); setTo(todayISO()); }} />
          <QuickDateBtn label="آخر ٣٠ يوم" onClick={() => { setFrom(daysAgoISO(29)); setTo(todayISO()); }} />
        </div>
        {/* Source filter */}
        <div className="flex flex-wrap gap-1.5">
          {SOURCES.map((s) => (
            <button key={s.id} onClick={() => setSource(s.id)}
              data-testid={`sales-src-${s.id}`}
              className={`text-xs font-bold py-1.5 px-3 rounded-full transition-colors ${
                source === s.id ? `${s.color} text-white` : "bg-[#F3F6F4] text-[#5C6E64]"
              }`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search + Export toolbar — ALWAYS visible so admin can find any old invoice */}
      <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3 flex flex-wrap gap-2 justify-between items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="sales-search"
            placeholder="🔍 بحث سريع: رقم فاتورة / اسم زبون / رقم — يبحث بكل الفواتير حتى القديمة"
            className="w-full pr-9 pl-3 py-2.5 rounded-xl border-2 border-[#F5A623]/50 focus:border-[#F5A623] bg-white text-sm font-bold focus:outline-none" />
          {q && (
            <button onClick={() => setQ("")} data-testid="sales-search-clear"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
              <span className="text-slate-500 text-xs">✕</span>
            </button>
          )}
        </div>
        {filtered.length > 0 && (
          <div className="flex gap-2">
            <button onClick={handlePrint} data-testid="sales-print"
              className="bg-[#1E3A5F] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5">
              <Printer className="w-3.5 h-3.5" /> طباعة
            </button>
            <button onClick={handlePrint} data-testid="sales-pdf"
              className="bg-[#B91C1C] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> PDF
            </button>
            <button onClick={handleWhatsApp} data-testid="sales-whatsapp"
              className="bg-[#25D366] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5" /> واتساب
            </button>
          </div>
        )}
      </div>

      {/* Search-mode banner: tell the admin we're searching across ALL time */}
      {q && !loading && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="sales-search-mode">
          <Search className="w-3.5 h-3.5" />
          <span>وضع البحث السريع — يشمل كل الفواتير مو محدد بالتاريخ. نتائج: <b>{filtered.length}</b> فاتورة</span>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#E4EBE6] rounded-2xl p-8 text-center text-[#5C6E64]">
          <ShoppingCart className="w-10 h-10 mx-auto opacity-30 mb-2" />
          <p className="font-bold">لا توجد فواتير في هذه الفترة</p>
        </div>
      ) : (
        <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="sales-table">
              <thead className="bg-[#1E3A5F] text-white">
                <tr>
                  <th className="p-2 text-center">#</th>
                  <th className="p-2 text-center">رقم</th>
                  <th className="p-2 text-center">التاريخ</th>
                  <th className="p-2 text-right">الزبون</th>
                  <th className="p-2 text-center">المصدر</th>
                  <th className="p-2 text-center">أصناف</th>
                  <th className="p-2 text-center">الإجمالي</th>
                  <th className="p-2 text-center">المدفوع</th>
                  <th className="p-2 text-center">المتبقي</th>
                  <th className="p-2 text-center">الحالة</th>
                  <th className="p-2 text-center print:hidden"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <SalesRow key={r.id} r={r} i={i} onView={() => openInvoice(r)} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Final summary blocks (blue + green + red) */}
      {data && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-5 shadow-lg" data-testid="sales-block-total">
            <div className="flex items-center gap-2 mb-1"><Wallet className="w-5 h-5" /><p className="font-bold text-sm">إجمالي المبيعات</p></div>
            <p className="text-3xl font-black" dir="ltr">{fmt(s.total_sales)} د.ع</p>
            <p className="text-[10px] opacity-90 mt-1"><span dir="ltr">{fmt(data.count)}</span> فاتورة</p>
          </div>
          <div className="bg-gradient-to-l from-[#15803D] to-[#22C55E] text-white rounded-2xl p-5 shadow-lg" data-testid="sales-block-received">
            <div className="flex items-center gap-2 mb-1"><span className="text-lg">✅</span><p className="font-bold text-sm">المسدَّد / المستلَم نقداً</p></div>
            <p className="text-3xl font-black" dir="ltr">{fmt(s.total_received)} د.ع</p>
            <p className="text-[10px] opacity-90 mt-1"><span dir="ltr">{fmt(s.paid_count)}</span> مسدَّدة كاملاً · <span dir="ltr">{fmt(s.partial_count)}</span> جزئي</p>
          </div>
          <div className="bg-gradient-to-l from-[#B91C1C] to-[#DC2626] text-white rounded-2xl p-5 shadow-lg" data-testid="sales-block-remaining">
            <div className="flex items-center gap-2 mb-1"><span className="text-lg">🔴</span><p className="font-bold text-sm">المتبقي (دين)</p></div>
            <p className="text-3xl font-black" dir="ltr">{fmt(s.total_remaining)} د.ع</p>
            <p className="text-[10px] opacity-90 mt-1"><span dir="ltr">{fmt(s.unpaid_count)}</span> فاتورة غير مسدَّدة</p>
          </div>
        </div>
      )}

      {/* View invoice modal */}
      {viewOrder && (
        <InvoiceModal
          order={viewOrder}
          customerInfo={{ full_name: viewOrder.customer_name, phone: viewOrder.customer_phone, market_name: viewOrder.customer_market_name }}
          onClose={() => setViewOrder(null)}
          viewOnly={true}
        />
      )}
    </div>
  );
};

const QuickDateBtn = ({ label, onClick }) => (
  <button onClick={onClick} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] rounded-full px-2 py-1 font-bold">
    {label}
  </button>
);

const SalesRow = ({ r, i, onView }) => {
  const dt = splitDate(r.created_at);
  const bg =
    r.source === "بيع المشرف" ? "bg-[#FAF5FF]" :
    r.source === "بيع مباشر" ? "bg-[#FFFBEB]" :
    "bg-[#EFF6FF]";
  const srcBadge =
    r.source === "بيع المشرف" ? "bg-[#EDE9FE] text-[#6D28D9]" :
    r.source === "بيع مباشر" ? "bg-[#FFF8EC] text-[#a06e10]" :
    "bg-[#DBEAFE] text-[#1E40AF]";
  const payBadge =
    r.payment_status === "paid" ? "bg-[#DCFCE7] text-[#15803D]" :
    r.payment_status === "partial" ? "bg-[#FEF3C7] text-[#B45309]" :
    "bg-[#FEE2E2] text-[#B91C1C]";
  const payLbl =
    r.payment_status === "paid" ? "✅ مسدَّدة" :
    r.payment_status === "partial" ? "🟠 جزئي" :
    "🔴 غير مسدَّدة";
  return (
    <tr className={`${bg} border-b border-[#E4EBE6] hover:brightness-95 transition-all`} data-testid={`sales-row-${r.id}`}>
      <td className="p-1.5 text-center text-[#5C6E64]" dir="ltr">{i + 1}</td>
      <td className="p-1.5 text-center font-black text-[#1E3A5F]" dir="ltr">#{r.invoice_number || "—"}</td>
      <td className="p-1.5 text-center" dir="ltr">
        <div className="font-bold text-[11px]">{dt.day}</div>
        <div className="text-[9px] text-[#5C6E64]">{dt.time}</div>
      </td>
      <td className="p-1.5">
        <div className="font-bold text-[#111814] truncate max-w-[160px]">{r.customer_name || "—"}</div>
        {r.customer_market_name && <div className="text-[10px] text-[#5C6E64] truncate max-w-[160px]">🏪 {r.customer_market_name}</div>}
        <div className="text-[10px] text-[#5C6E64]" dir="ltr">{r.customer_phone}</div>
      </td>
      <td className="p-1.5 text-center">
        <span className={`text-[10px] rounded-full px-2 py-0.5 font-bold ${srcBadge}`}>{r.source}</span>
        {r.source_extra && <div className="text-[9px] text-[#5C6E64] mt-0.5 truncate">{r.source_extra}</div>}
      </td>
      <td className="p-1.5 text-center font-bold" dir="ltr">{r.items_count}</td>
      <td className="p-1.5 text-center font-black text-[#1E3A5F]" dir="ltr">{fmt(r.total)}</td>
      <td className="p-1.5 text-center font-bold text-[#15803D]" dir="ltr">{fmt(r.paid)}</td>
      <td className="p-1.5 text-center font-bold text-[#B91C1C]" dir="ltr">{fmt(r.remaining)}</td>
      <td className="p-1.5 text-center">
        <span className={`text-[10px] rounded-full px-2 py-0.5 font-bold whitespace-nowrap ${payBadge}`}>{payLbl}</span>
      </td>
      <td className="p-1.5 text-center print:hidden">
        <button onClick={onView} data-testid={`sales-view-${r.id}`}
          className="bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-lg p-1.5" title="عرض الفاتورة">
          <Eye className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
};

export default SalesInvoicesReportTab;
